import { useCallback, useState, type JSX } from 'react';
import { toast } from 'sonner';
import { Form, FormActions, FormMessage } from '@/components/form';
import { PillButton } from '@/components/entity/PillButton';
import { useDashboardUser } from '@/routes/dashboard/DashboardRouter';
import { useUnitCreateMutation } from '@/hooks/useDashboardProperties';
import { unitFormSchema } from '@/lib/validation/property';
import type { UnitFormValues } from '@/lib/types/property';
import { UnitFormFields } from '@/routes/dashboard/unitForm/UnitFormFields';
import { formatUnitLabel } from '@/lib/utils/propertyFormatting';
import {
  buildUnitPayload,
  createUnitEmptyState,
  mapUnitIssues,
  type UnitFormErrors,
  type UnitFormState,
} from '@/routes/dashboard/unitForm/unitFormState';

export type UnitCreateNextStep = 'createAnother' | 'viewUnit' | 'backToProperty';

export interface UnitCreateFormProps {
  propertyId: string;
  onCancel: () => void;
  onCreated: (created: UnitFormValues, nextStep: UnitCreateNextStep) => void;
}

export function UnitCreateForm({
  propertyId,
  onCancel,
  onCreated,
}: UnitCreateFormProps): JSX.Element {
  const user = useDashboardUser();
  const createMutation = useUnitCreateMutation({ user, propertyId });

  const [formState, setFormState] = useState<UnitFormState>(createUnitEmptyState);
  const [errors, setErrors] = useState<UnitFormErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isUploadingHero, setIsUploadingHero] = useState(false);
  const [createAnother, setCreateAnother] = useState(false);
  const [viewOnSuccess, setViewOnSuccess] = useState(false);

  const clearFieldError = useCallback((field: keyof UnitFormState) => {
    setErrors((current) => {
      if (current[field] === undefined) {
        return current;
      }
      const { [field]: _removed, ...rest } = current;
      return rest;
    });
  }, []);

  const disabled = createMutation.isPending;

  const handleSubmit = useCallback(() => {
    setFormError(null);
    setErrors({});

    if (isUploadingHero) {
      setFormError('Wait for the hero image upload to finish.');
      return;
    }

    const payload = buildUnitPayload({ propertyId, state: formState });
    const validation = unitFormSchema.safeParse(payload);

    if (!validation.success) {
      setErrors(mapUnitIssues(validation.error.issues));
      const firstMessage = validation.error.issues.at(0)?.message ?? 'Fix the highlighted fields.';
      setFormError(firstMessage);
      return;
    }

    createMutation.mutate(
      { values: validation.data },
      {
        onSuccess: (created) => {
          toast.success(`${formatUnitLabel(created)} created.`);
          const nextStep: UnitCreateNextStep = createAnother
            ? 'createAnother'
            : viewOnSuccess
              ? 'viewUnit'
              : 'backToProperty';

          if (nextStep === 'createAnother') {
            setFormState(createUnitEmptyState());
            setErrors({});
            setFormError(null);
          }

          onCreated(created, nextStep);
        },
        onError: (error) => {
          const message = error instanceof Error ? error.message : 'Unable to create unit.';
          setFormError(message);
          toast.error('Unable to create unit.', { description: message });
        },
      },
    );
  }, [
    createAnother,
    createMutation,
    formState,
    isUploadingHero,
    onCreated,
    propertyId,
    viewOnSuccess,
  ]);

  return (
    <Form
      gap="lg"
      onSubmit={(event) => {
        if (!event.currentTarget.reportValidity()) {
          return;
        }
        event.preventDefault();
        handleSubmit();
      }}
    >
      {formError !== null ? (
        <FormMessage tone="error" role="alert">
          {formError}
        </FormMessage>
      ) : null}

      <UnitFormFields
        propertyId={propertyId}
        formState={formState}
        errors={errors}
        disabled={disabled}
        isUploadingHero={isUploadingHero}
        setFormState={setFormState}
        clearFieldError={clearFieldError}
        onUploadingHeroChange={setIsUploadingHero}
      />

      <FormActions justify="between">
        <div className="flex flex-col gap-2 text-sm font-semibold text-text-primary/80">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={createAnother}
              onChange={(event) => {
                const next = event.target.checked;
                setCreateAnother(next);
                if (next) {
                  setViewOnSuccess(false);
                }
              }}
              disabled={disabled}
              className="h-4 w-4 rounded border-surface-subtle text-brand-primary focus:ring-brand-primary"
            />
            Create another unit immediately after success
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={viewOnSuccess}
              onChange={(event) => {
                const next = event.target.checked;
                setViewOnSuccess(next);
                if (next) {
                  setCreateAnother(false);
                }
              }}
              disabled={disabled}
              className="h-4 w-4 rounded border-surface-subtle text-brand-primary focus:ring-brand-primary"
            />
            View the unit on success
          </label>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            className="rounded-full px-4 py-2 text-sm font-semibold text-text-primary/70 transition hover:bg-surface-subtle disabled:opacity-60"
            onClick={onCancel}
            disabled={disabled}
          >
            Cancel
          </button>
          <PillButton type="submit" disabled={disabled || isUploadingHero}>
            {disabled ? 'Creating…' : 'Create unit'}
          </PillButton>
        </div>
      </FormActions>
    </Form>
  );
}
